import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AirdropRecipient } from '@/components/Airdrop/utils'
import RootButton from '@/components/Buttons/RootButton'
import {
  ConfirmationError,
  ConfirmationLoading,
  ConfirmationReady,
} from '@/components/Identity/ConfirmModals/utils'
import TransactionSummary from '@/components/Transfer/TransactionSummary'
import { AIRDROP_CONTRACT_ADDRESS, NATIVE_CHAIN_LAUNCH_TICKER } from '@/constants/blockchain'
import ClaimableAirdrop from '@/contracts/ClaimableAirdrop'
import { ContractParams, useChainFunctions } from '@/hooks/useChain'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useAuth } from '@/plugins/auth'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'
import { processBalance } from '@/utils/utils'

interface ClaimAirdropModalProps {
  close: () => void
  nextStep: () => void
  claimableRewards: bigint
}

const ClaimAirdropModal: React.FC<ClaimAirdropModalProps> = ({
  close,
  nextStep,
  claimableRewards,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { address } = useAuth()

  const [mergedRewards] = useSessionStorage<AirdropRecipient[]>(`mergedRewards-${address}`)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  const claimRewardsConfig: ContractParams | undefined = useMemo(() => {
    if (!mergedRewards) {
      return undefined
    }
    return {
      address: AIRDROP_CONTRACT_ADDRESS as `0x${string}`,
      abi: ClaimableAirdrop,
      functionName: 'claimMyRewards',
      args: [
        mergedRewards.map(({ AirdropID }) => AirdropID),
        mergedRewards.map(({ NodeID }) => NodeID),
        mergedRewards.map(({ RewardAmount }) => RewardAmount),
        mergedRewards.map(({ MerkleProof }) => MerkleProof),
      ],
    }
  }, [mergedRewards])

  const handleClaimAirdropModal = useCallback(async () => {
    if (!claimRewardsConfig) return
    setIsError('')
    try {
      setIsLoading(true)
      await writeContract(claimRewardsConfig)
      setIsLoading(false)
      setIsReady(true)
    } catch (error: any) {
      console.error(error)
      setIsLoading(false)
      setIsError(error.message)
    }
  }, [claimRewardsConfig, writeContract])

  return (
    <>
      {isError && <ConfirmationError title={isError} retry={handleClaimAirdropModal} />}
      {isLoading && (
        <ConfirmationLoading
          title={t('components.airdrop.modal.claim.loadingMessage', 'Validating Claim')}
        />
      )}
      {isReady && !isLoading && !isError && (
        <ConfirmationReady
          message={t('components.airdrop.modal.claim.readyMessage', 'Airdrop Claim Submitted')}
          tip={t(
            'components.airdrop.modal.claim.tipMessage',
            'Check Wallet History for Transaction Status'
          )}
          success={nextStep}
        />
      )}
      {!isReady && !isLoading && !isError && (
        <Stack>
          <Text style={{ marginBottom: '20px' }}>
            {t(
              'components.airdrop.modal.claim.info',
              `Note: If you don’t claim these rewards by November 29, 2024, you may not receive an airdrop.`
            )}
          </Text>

          <Stack
            style={{
              backgroundColor: theme.colors.background3[0],
              borderRadius: '12px',
              padding: '16px',
              width: 'fit-content',
              gap: 20,
            }}
          >
            <Title order={4}>
              {processBalance(claimableRewards).display} {NATIVE_CHAIN_LAUNCH_TICKER}
            </Title>
          </Stack>
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={close} secondary style1={false} expand>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton
              onClick={handleClaimAirdropModal}
              style1={false}
              expand
              disabled={claimableRewards === BigInt(0)}
            >
              {t('buttons.confirm', 'Confirm')}
            </RootButton>
          </Stack>
          <TransactionSummary />
        </Stack>
      )}
    </>
  )
}
export default ClaimAirdropModal
